import { AnchorDaysObject } from './AnchorDayPicker';
import {
  isBefore,
  isAfter,
  addDays,
  startOfWeek,
  eachDayOfInterval
} from 'date-fns';
import { WeekDayNames } from '@gettactic/api';

export const weekendDays: WeekDayNames[] = ['saturday', 'sunday'];

export const getAvailableWeekDays = ({
  minDate,
  maxDate,
  defaultAnchorDays
}: {
  minDate: Date | undefined;
  maxDate: Date | undefined;
  defaultAnchorDays: AnchorDaysObject;
}) => {
  const availableDays = { ...defaultAnchorDays };

  if (minDate && maxDate) {
    try {
      const daysInRange = eachDayOfInterval({ start: minDate, end: maxDate });

      daysInRange.forEach((date) => {
        const dayIndex = date.getDay();
        const dayName = weekDays[dayIndex].value;
        availableDays[dayName] = true;
      });
    } catch (err) {
      weekDays.forEach((day) => {
        availableDays[day.value] = true;
      });
    }
  }

  else if (minDate) {
    const today = new Date();
    const startOfCurrentWeek = startOfWeek(today);
    const daysToCheck = 7;

    for (let i = 0; i < daysToCheck; i++) {
      const day = addDays(startOfCurrentWeek, i);
      if (!isBefore(day, minDate)) {
        const dayIndex = day.getDay();
        const dayName = weekDays[dayIndex].value;
        availableDays[dayName] = true;
      }
    }
  } else if (maxDate) {
    const today = new Date();
    const startOfCurrentWeek = startOfWeek(today);
    const daysToCheck = 7;

    for (let i = 0; i < daysToCheck; i++) {
      const day = addDays(startOfCurrentWeek, i);
      if (!isAfter(day, maxDate)) {
        const dayIndex = day.getDay();
        const dayName = weekDays[dayIndex].value;
        availableDays[dayName] = true;
      }
    }
  }

  else {
    weekDays.forEach((day) => {
      availableDays[day.value] = true;
    });
  }

  return availableDays;
};

export const weekDays: { label: string, value: WeekDayNames }[] = [
  { label: 'Sunday', value: 'sunday' },
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' }
];

export const getCurrentDayAsString = (idx: number): string => {
  return weekDays[idx].value;
};

// Helper function to determine if a day represents a future week
// when comparing with the current day index
export const isDayInNextWeek = (currentDayIdx: number, dayIdx: number): boolean => {
  // If the day index is less than the current day index,
  // it must be in a future week (e.g., Monday (1) compared to Thursday (4))
  return dayIdx < currentDayIdx;
};

export const isDayDisabled = (currentDayIdx: number, day: WeekDayNames, minDate: Date | undefined, maxDate: Date | undefined, availableWeekdays: AnchorDaysObject): boolean => {
    const currentDayString = getCurrentDayAsString(currentDayIdx);
    if (!maxDate) {
      return minDate ? !availableWeekdays[day] : false;
    }

    const dayIndex = weekDays.findIndex((d) => d.value === day);
    const currentDayIndex = weekDays.findIndex(
      (d) => d.value === currentDayString
    );
    
    const dayInWeekday: WeekDayNames = day.toLowerCase() as WeekDayNames;
    const isNextWeekDay = isDayInNextWeek(currentDayIndex, dayIndex);
    if (isNextWeekDay && availableWeekdays[dayInWeekday]) {
      return false; // Don't disable days in the next week that are available
    }
    
    const isBeforeCurrentDay = dayIndex < currentDayIndex;
    const isAvailable = availableWeekdays[day];

    return isBeforeCurrentDay || !isAvailable;
  };